import { languageMap } from './constants';

// Generate iframe code for embedding
export const generateIframeCode = (hlsUrl, dashUrl, fileUrl, srtCaptions, vttCaptions) => {
    // Prioritize HLS, then DASH, then file URL
    const videoUrl = hlsUrl || dashUrl || fileUrl;
    if (!videoUrl) return '';
    
    // Get caption tracks from API response - support both VTT and SRT formats
    // Combine both VTT and SRT captions, prioritizing VTT for each language
    const allCaptionTracks = [];
    
    // First, add all VTT captions (natively supported by HTML5)
    vttCaptions.forEach(caption => {
        allCaptionTracks.push({
            kind: 'subtitles',
            src: caption.url,
            srclang: caption.language,
            label: languageMap[caption.language] || caption.language.toUpperCase(),
            format: 'vtt'
        });
    });
    
    // Then add SRT captions for languages that don't have VTT
    srtCaptions.forEach(caption => {
        const hasVTTForLang = vttCaptions.some(vtt => vtt.language === caption.language);
        if (!hasVTTForLang) {
            allCaptionTracks.push({
                kind: 'subtitles',
                src: caption.url,
                srclang: caption.language,
                label: languageMap[caption.language] || caption.language.toUpperCase(),
                format: 'srt'
            });
        }
    });
    
    // Set default to first English track, or first track if no English
    const captionTracks = allCaptionTracks.map((track, index) => {
        const isDefault = index === 0 || (track.srclang === 'en' && !allCaptionTracks.slice(0, index).some(t => t.srclang === 'en'));
        return { ...track, default: isDefault };
    });
    
    // Generate caption tracks HTML - VTT tracks use native support, SRT will be handled by JavaScript
    const captionTracksHtml = captionTracks.map((track) => {
        if (track.format === 'vtt') {
            // Native VTT support
            return `<track kind="${track.kind}" src="${track.src}" srclang="${track.srclang}" label="${track.label}" ${track.default ? 'default' : ''}>`;
        } else {
            // SRT tracks - will be converted to VTT by JavaScript
            return `<track kind="${track.kind}" data-srt-url="${track.src}" srclang="${track.srclang}" label="${track.label}" ${track.default ? 'default' : ''} data-format="srt">`;
        }
    }).join('\n                        ');
    
    // Determine video format
    const isHLS = videoUrl.includes('.m3u8');
    const isDASH = videoUrl.includes('.mpd');
    
    // Generate iframe HTML code with video player
    let iframeCode = '';
    
    if (isHLS) {
        // For HLS, use hls.js player
        const videoHtml = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <style>
                    body { margin: 0; padding: 0; background: #000; display: flex; justify-content: center; align-items: center; height: 100vh; }
                    video { width: 100%; height: 100%; max-width: 100%; max-height: 100%; }
                </style>
                <script src="https://cdn.jsdelivr.net/npm/hls.js@latest"></script>
            </head>
            <body>
                <video id="video" controls autoplay>
                    ${captionTracksHtml}
                </video>
                <script>
                    // Function to convert SRT to VTT format
                    function srtToVtt(srtText) {
                        var vtt = 'WEBVTT\\n\\n';
                        var lines = srtText.split(/\\r?\\n/);
                        var i = 0;
                        while (i < lines.length) {
                            var line = lines[i].trim();
                            if (line && !isNaN(line)) {
                                // Skip sequence number
                                i++;
                                if (i < lines.length) {
                                    var timecode = lines[i].trim();
                                    if (timecode.includes('-->')) {
                                        // Convert timecode format from SRT to VTT
                                        timecode = timecode.replace(',', '.');
                                        vtt += timecode + '\\n';
                                        i++;
                                        var text = '';
                                        while (i < lines.length && lines[i].trim()) {
                                            text += lines[i].trim() + '\\n';
                                            i++;
                                        }
                                        vtt += text + '\\n';
                                    }
                                }
                            }
                            i++;
                        }
                        return vtt;
                    }
                    
                    // Convert SRT tracks to VTT
                    function convertSrtTracks() {
                        var video = document.getElementById('video');
                        var srtTracks = video.querySelectorAll('track[data-format="srt"]');
                        srtTracks.forEach(function(track) {
                            var srtUrl = track.getAttribute('data-srt-url');
                            fetch(srtUrl)
                                .then(function(response) { return response.text(); })
                                .then(function(srtText) {
                                    var vttText = srtToVtt(srtText);
                                    var blob = new Blob([vttText], { type: 'text/vtt' });
                                    var vttUrl = URL.createObjectURL(blob);
                                    track.src = vttUrl;
                                    track.removeAttribute('data-format');
                                    track.removeAttribute('data-srt-url');
                                })
                                .catch(function(error) {
                                    console.error('Error converting SRT to VTT:', error);
                                });
                        });
                    }
                    
                    var video = document.getElementById('video');
                    var videoSrc = '${videoUrl}';
                    if (Hls.isSupported()) {
                        var hls = new Hls();
                        hls.loadSource(videoSrc);
                        hls.attachMedia(video);
                    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                        video.src = videoSrc;
                    }
                    
                    // Convert SRT tracks after video loads
                    video.addEventListener('loadedmetadata', convertSrtTracks);
                </script>
            </body>
            </html>
        `;
        // Encode HTML for data URI and create iframe
        const encodedHtml = encodeURIComponent(videoHtml.trim());
        // Create display version (shorter, more readable) and actual code (full encoded)
        const displayCode = `<iframe src="data:text/html;charset=utf-8,[ENCODED_HTML_CONTENT]" width="100%" height="500" frameborder="0" allowfullscreen title="Video Player"></iframe>`;
        const actualCode = `<iframe src="data:text/html;charset=utf-8,${encodedHtml}" width="100%" height="500" frameborder="0" allowfullscreen title="Video Player"></iframe>`;
        iframeCode = { display: displayCode, actual: actualCode };
    } else if (isDASH) {
        // For DASH, use dash.js player
        const videoHtml = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <style>
                    body { margin: 0; padding: 0; background: #000; display: flex; justify-content: center; align-items: center; height: 100vh; }
                    video { width: 100%; height: 100%; max-width: 100%; max-height: 100%; }
                </style>
                <script src="https://cdn.dashjs.org/latest/dash.all.min.js"></script>
            </head>
            <body>
                <video id="video" controls autoplay>
                    ${captionTracksHtml}
                </video>
                <script>
                    // Function to convert SRT to VTT format
                    function srtToVtt(srtText) {
                        var vtt = 'WEBVTT\\n\\n';
                        var lines = srtText.split(/\\r?\\n/);
                        var i = 0;
                        while (i < lines.length) {
                            var line = lines[i].trim();
                            if (line && !isNaN(line)) {
                                // Skip sequence number
                                i++;
                                if (i < lines.length) {
                                    var timecode = lines[i].trim();
                                    if (timecode.includes('-->')) {
                                        // Convert timecode format from SRT to VTT
                                        timecode = timecode.replace(',', '.');
                                        vtt += timecode + '\\n';
                                        i++;
                                        var text = '';
                                        while (i < lines.length && lines[i].trim()) {
                                            text += lines[i].trim() + '\\n';
                                            i++;
                                        }
                                        vtt += text + '\\n';
                                    }
                                }
                            }
                            i++;
                        }
                        return vtt;
                    }
                    
                    // Convert SRT tracks to VTT
                    function convertSrtTracks() {
                        var video = document.getElementById('video');
                        var srtTracks = video.querySelectorAll('track[data-format="srt"]');
                        srtTracks.forEach(function(track) {
                            var srtUrl = track.getAttribute('data-srt-url');
                            fetch(srtUrl)
                                .then(function(response) { return response.text(); })
                                .then(function(srtText) {
                                    var vttText = srtToVtt(srtText);
                                    var blob = new Blob([vttText], { type: 'text/vtt' });
                                    var vttUrl = URL.createObjectURL(blob);
                                    track.src = vttUrl;
                                    track.removeAttribute('data-format');
                                    track.removeAttribute('data-srt-url');
                                })
                                .catch(function(error) {
                                    console.error('Error converting SRT to VTT:', error);
                                });
                        });
                    }
                    
                    var video = document.getElementById('video');
                    var videoSrc = '${videoUrl}';
                    var player = dashjs.MediaPlayer().create();
                    player.initialize(video, videoSrc, true);
                    
                    // Convert SRT tracks after video loads
                    video.addEventListener('loadedmetadata', convertSrtTracks);
                </script>
            </body>
            </html>
        `;
        // Encode HTML for data URI and create iframe
        const encodedHtml = encodeURIComponent(videoHtml.trim());
        // Create display version (shorter, more readable) and actual code (full encoded)
        const displayCode = `<iframe src="data:text/html;charset=utf-8,[ENCODED_HTML_CONTENT]" width="100%" height="500" frameborder="0" allowfullscreen title="Video Player"></iframe>`;
        const actualCode = `<iframe src="data:text/html;charset=utf-8,${encodedHtml}" width="100%" height="500" frameborder="0" allowfullscreen title="Video Player"></iframe>`;
        iframeCode = { display: displayCode, actual: actualCode };
    } else {
        // For MP4, use HTML5 video player
        const videoHtml = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <style>
                    body { margin: 0; padding: 0; background: #000; display: flex; justify-content: center; align-items: center; height: 100vh; }
                    video { width: 100%; height: 100%; max-width: 100%; max-height: 100%; }
                </style>
            </head>
            <body>
                <video id="video" controls autoplay>
                    <source src="${videoUrl}" type="video/mp4">
                    ${captionTracksHtml}
                    Your browser does not support the video tag.
                </video>
                <script>
                    // Function to convert SRT to VTT format
                    function srtToVtt(srtText) {
                        var vtt = 'WEBVTT\\n\\n';
                        var lines = srtText.split(/\\r?\\n/);
                        var i = 0;
                        while (i < lines.length) {
                            var line = lines[i].trim();
                            if (line && !isNaN(line)) {
                                // Skip sequence number
                                i++;
                                if (i < lines.length) {
                                    var timecode = lines[i].trim();
                                    if (timecode.includes('-->')) {
                                        // Convert timecode format from SRT to VTT
                                        timecode = timecode.replace(',', '.');
                                        vtt += timecode + '\\n';
                                        i++;
                                        var text = '';
                                        while (i < lines.length && lines[i].trim()) {
                                            text += lines[i].trim() + '\\n';
                                            i++;
                                        }
                                        vtt += text + '\\n';
                                    }
                                }
                            }
                            i++;
                        }
                        return vtt;
                    }
                    
                    // Convert SRT tracks to VTT
                    function convertSrtTracks() {
                        var video = document.getElementById('video');
                        var srtTracks = video.querySelectorAll('track[data-format="srt"]');
                        srtTracks.forEach(function(track) {
                            var srtUrl = track.getAttribute('data-srt-url');
                            fetch(srtUrl)
                                .then(function(response) { return response.text(); })
                                .then(function(srtText) {
                                    var vttText = srtToVtt(srtText);
                                    var blob = new Blob([vttText], { type: 'text/vtt' });
                                    var vttUrl = URL.createObjectURL(blob);
                                    track.src = vttUrl;
                                    track.removeAttribute('data-format');
                                    track.removeAttribute('data-srt-url');
                                })
                                .catch(function(error) {
                                    console.error('Error converting SRT to VTT:', error);
                                });
                        });
                    }
                    
                    var video = document.getElementById('video');
                    // Convert SRT tracks after video loads
                    video.addEventListener('loadedmetadata', convertSrtTracks);
                </script>
            </body>
            </html>
        `;
        // Encode HTML for data URI and create iframe
        const encodedHtml = encodeURIComponent(videoHtml.trim());
        // Create display version (shorter, more readable) and actual code (full encoded)
        const displayCode = `<iframe src="data:text/html;charset=utf-8,[ENCODED_HTML_CONTENT]" width="100%" height="500" frameborder="0" allowfullscreen title="Video Player"></iframe>`;
        const actualCode = `<iframe src="data:text/html;charset=utf-8,${encodedHtml}" width="100%" height="500" frameborder="0" allowfullscreen title="Video Player"></iframe>`;
        iframeCode = { display: displayCode, actual: actualCode };
    }
    
    // If iframeCode is still a string (shouldn't happen, but handle it), return object format
    if (typeof iframeCode === 'string') {
        return { display: iframeCode, actual: iframeCode };
    }
    
    return iframeCode;
};

